import { gql, useMutation } from '@apollo/client'

import {
  CollectionsSelectBoxFragmentDoc,
  CreateCollectionMutation,
  CreateCollectionMutationVariables,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import {
  useMessage,
  useRouter,
  useTranslation,
  useUserTmpUploader,
} from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'
import { Camera } from '@/core/native'
import { createUuid } from '@/lib/uuid'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const { showError, showMessage } = useMessage()
  const { router, routerPages } = useRouter()
  const { t } = useTranslation('consoleCollectionCreate')
  const { getImage } = Camera
  const { upload, progress } = useUserTmpUploader(user?.uid || '')

  const [createCollection] = useMutation<
    CreateCollectionMutation,
    CreateCollectionMutationVariables
  >(CREATE_COLLECTION, {
    onCompleted() {
      showMessage(t('createdCollectionSuccessful'), 'success')
      router.goBack(routerPages.ConsoleCollections)
    },
  })

  const onDrop = async () => {
    const file = await getImage(config.upload.mimeTypes.image)
    if (file.size > config.upload.limit.image) {
      throw new Error('uploadErrorMaximumSize')
    }
    return file
  }

  return {
    state: {
      artistId: user?.uid ?? '',
      progress,
    },
    actions: {
      async onDrop() {
        const file = await onDrop().catch(error => {
          showError(new Error(t([error.message, 'interruptedUpload'])))
        })
        if (!file) {
          return
        }
        const { tmpPath, fileUrl } = await upload(file, false, true)
        const uuid = createUuid()
        return {
          id: uuid,
          resourceName: uuid,
          imageUrl: fileUrl,
          imageUploadPath: tmpPath,
        }
      },
      async onSubmit({
        title,
        description,
        nft,
        thumbnailPath,
        isPublishOnStore,
        startedAt,
        endedAt,
        status,
      }) {
        await createCollection({
          variables: {
            inputs: {
              title,
              description,
              nft,
              thumbnailPath,
              isPublishOnStore,
              startedAt,
              endedAt,
              status,
            },
          },
        })
      },
    },
  }
})

const CREATE_COLLECTION = gql`
  mutation CreateCollection($inputs: CreateCollectionInput) {
    createCollection(inputs: $inputs) {
      ...CollectionsSelectBox
    }
  }
  ${CollectionsSelectBoxFragmentDoc}
`
